import React, { useEffect, useState } from 'react';
import { useParams, Link  } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ChevronDownIcon, NewspaperIcon, QuestionMarkCircleIcon, BookOpenIcon} from '@heroicons/react/24/outline';
import ReviewsSlider from '../components/Reviews';
import SubTopicsGrid from '../components/SubTopicsGrid';
import ContentRenderer from './admin/ContentRenderer';
import { ArrowLeft ,CheckCircle, Clock, Shield,Phone} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { FaWhatsapp ,FaWaze, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import AnimatedHeader from '../components/AnimatedHeader';
import ProcessSteps from '../components/ProcessSteps';
import HeaderLayout from '../components/HeaderLayout';
import ReasonsList from '../components/ReasonsCard';
import ResponsiveImage from '../components/ResponsiveImage';

const MAX_CONTENT_LENGTH = 200; // מספר התווים המקסימלי לתקציר
const topicIdMapping = {
  realestate: 'Cq3auS1VsmtbG10Kw2ef',
  execution: 'niwjxuloe5cfnBVAIxPr',
  damages: 'vzOfLHY5Y0lGDXpQbnie'
};

const createSummary = (content) => {
  if (!content) return '';
  const strippedContent = content.replace(/<[^>]+>/g, '');
  if (strippedContent.length <= MAX_CONTENT_LENGTH) return content;
  return strippedContent.substring(0, MAX_CONTENT_LENGTH) + '...';
};

const TopicPage = () => {
  const { topicId } = useParams();
  const [topicData, setTopicData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQuestion, setActiveQuestion] = useState(null);
  
  useEffect(() => {
    // גלילה לחלק העליון של הדף
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // לגלילה חלקה
    });
  }, [topicId]); // עכשיו יופעל בכל שינוי של topicId
  
  useEffect(() => {
    const fetchTopicData = async () => {
      try {
        const mappedTopicId = topicIdMapping[topicId];
        if (!mappedTopicId) {
          setTopicData(null);
          setLoading(false);
          return;
        }

        const topicDoc = await getDoc(doc(db, 'subjects', mappedTopicId));
        if (topicDoc.exists()) {
          setTopicData(topicDoc.data());

          const questionsQuery = query(
            collection(db, 'questions'),
            where('subjectId', '==', mappedTopicId)
          );
          const questionsSnapshot = await getDocs(questionsQuery);
          setQuestions(
            questionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          );

          const articlesQuery = query(
            collection(db, 'articles'),
            where('subjectId', '==', mappedTopicId)
          );
          const articlesSnapshot = await getDocs(articlesQuery);
          setArticles(
            articlesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          );
        } else {
          setTopicData(null);
        }
      } catch (error) {
        console.error('Error fetching topic data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopicData();
  }, [topicId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!topicData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8 bg-red-50 rounded-lg">
          <h2 className="text-2xl font-bold text-red-800">הנושא לא נמצא</h2>
          <p className="text-red-600 mt-2">נא לבדוק את הקישור ולנסות שוב</p>
        </div>
      </div>
    );
  }

  const QuestionCard = ({ question, answer, isActive, onClick }) => (
    <div className="bg-white rounded-lg shadow-md mb-4 overflow-hidden">
      <button
        onClick={onClick}
        className="w-full text-right px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex-1 text-right">
          <ContentRenderer content={question} />
        </div>
        <ChevronDownIcon
          className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${
            isActive ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      {isActive && (
        <div className="px-6 py-4 bg-gray-50 border-t text-right dir-rtl">
          <ContentRenderer content={answer} />
        </div>
      )}
    </div>
  );

  const ArticleCard = ({ title, content, articleId, image }) => (
    <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 flex flex-col h-[550px]"> {/* הוספנו גובה קבוע ו-flex column */}
      {/* Article Image - גובה קבוע לתמונה */}
      <div className="w-full h-52 flex-shrink-0"> {/* flex-shrink-0 מונע מהתמונה להתכווץ */}
        <img
          src={image || "/articlesImages/articleImage1.jpg"}
          alt={title}
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Article Content - משתמשים בשאר המקום הפנוי */}
      <div className="flex flex-col flex-grow p-6">
        {/* כותרת עם גובה מקסימלי */}
        <h3 className="text-xl font-bold text-gray-900 mb-4 line-clamp-2">
          <ContentRenderer content={title} />
        </h3>
        
        {/* תקציר - ממלא את החלל הנותר */}
        <div className="text-gray-600 mb-4 line-clamp-4 flex-grow">
          <ContentRenderer content={createSummary(content)} />
        </div>
  
        {/* כפתור תמיד בתחתית */}
        <Link 
          to={`/topic/${topicId}/${articleId}`}
          className="w-full text-center py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-700 transition-colors mt-auto"
        >
          קרא עוד
        </Link>
      </div>
    </div>
  );

  return (
    <div className="flex-grow bg-gray-100 [background-image:repeating-linear-gradient(60deg,rgba(53,67,78,0.1),rgba(53,67,78,0.1)_1px,transparent_1px,transparent_30px)] [background-size:60px_60px]">
 
    <HeaderLayout topicData={topicData} />
    <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header Section */}
    
        {/* Sub Topics Section */}
        {topicData.subSubjects && topicData.subSubjects.length > 0 && (
          <SubTopicsGrid subSubjects={topicData.subSubjects} />
        )}
        
        <ProcessSteps/>
        
        {/* Content Section - Modified for mobile-specific text styling */}
        <div className="text-left sm:text-center p-4 sm:p-14">
          <div className="sm:text-lg">
            <ContentRenderer content={topicData.generalContent} />
          </div>
        </div>
 
        <div className="text-center pb-0 mb-6">
          <div className="mt-8 bg-white p-6 rounded-xl shadow-md max-w-xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">
              צריכים עזרה בנושא {topicData.name}?
            </h3>
            <p className="text-gray-600 mb-4">
              השאירו פרטים לייעוץ ראשוני ללא התחייבות
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center px-8 py-4 text-lg font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl group gap-2"
            >
              <span>השאירו פרטים לייעוץ ראשוני</span>
              <ArrowLeft className="w-5 h-5 transform transition-transform group-hover:-translate-x-1" />
            </Link>
            <div className="mt-4 flex items-center justify-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>ללא עלות</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-blue-500" />
                <span>מענה מהיר</span>
              </div>
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-purple-500" />
                <span>דיסקרטיות מלאה</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Articles Section */}
        <div>
          <div className="flex items-center mb-6 p-10">
            <NewspaperIcon className="w-6 h-6 text-[#64748b] ml-2" />
            <h2 className="text-2xl font-semibold text-gray-900">מאמרים קשורים</h2>
          </div>
          
          {articles.length > 0 ? (
            <div className="relative">
              <div className="flex overflow-x-auto gap-6 pb-4 md:grid md:grid-cols-3 snap-x snap-mandatory hide-scrollbar">
                {articles.map(article => (
                  <div key={article.id} className="w-[280px] md:w-auto flex-shrink-0 snap-center">
                    <ArticleCard
                      title={article.title}
                      content={article.content}
                      articleId={article.id}
                      image={article.imageUrl}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-600 text-center py-8">אין מאמרים בנושא זה כרגע.</p>
          )}
        </div>

        <ReasonsList/>
        
        {/* Questions Section */}
        <div className="mb-12">
          <div className="flex items-center mb-6">
            <QuestionMarkCircleIcon className="w-6 h-6 text-[#64748b] ml-2" />
            <h2 className="text-2xl font-semibold text-gray-900">שאלות נפוצות שחשוב שתדעו:</h2>
          </div>
          {questions.length > 0 ? (
            <div className="space-y-4">
              {questions.map(q => (
                <QuestionCard
                  key={q.id}
                  question={q.question}
                  answer={q.answer}
                  isActive={activeQuestion === q.id}
                  onClick={() => setActiveQuestion(activeQuestion === q.id ? null : q.id)}
                />
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center py-8">אין שאלות בנושא זה כרגע.</p>
          )}
        </div>
     
        {/* Reviews Section */}
        <ReviewsSlider
          subjectId={topicIdMapping[topicId]}
          showTitle={true}
          title={`המלצות בתחום ${topicData.name}`}
          maxReviews={50}
          showControllers={true}
        />

        {/* Header Section with CTA Box */}
        <div className="text-center py-5">
          <Link 
            to="/contact" 
            className="inline-flex items-center px-8 py-4 text-lg font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl group gap-2"
          >
            <span>השאירו פרטים לייעוץ ראשוני</span>
            <ArrowLeft className="w-5 h-5 transform transition-transform group-hover:-translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TopicPage;